import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { Organisation, NewOrganisation, SingleOrgSummary } from '../models/response-models/organisation/organisation';
import { Address } from '../models/response-models/organisation/address';
import { Contact } from '../models/response-models/organisation/contact';
import { Country } from '../models/response-models/organisation/country';
import { BusinessType } from '../models/response-models/organisation/business-type';
import { UntypedFormGroup } from '@angular/forms';
import { Email } from '../models/response-models/organisation/email';
import { Phone } from '../models/response-models/organisation/phone';
import { WeekDay } from '../models/response-models/organisation/week-day';
import { Calendar } from '../models/response-models/organisation/calendar';
import { DeclarationRule } from '../models/response-models/organisation/declaration-rule';
import { OrganisationSetting } from '../models/response-models/organisation/organisation-setting';
import { OrderMode } from '../models/response-models/organisation/order-mode';
import { TradingSession } from '../models/response-models/organisation/trading-session';
import { Site } from '../models/response-models/site';
import { LoginService } from './login.service';
import { ErrorHandlerService } from './error-handler.service';
import { AlertSettings, AlertType } from '../alert/alert.service';
import { OrganisationSummary } from '../models/response-models/organisation/organisation-summary';
import moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class OrganisationService {
  loadingOrganisation: boolean;
  organisation: Organisation;
  organisationError: AlertSettings;

  constructor(
    private http: HttpClient,
    private loginService: LoginService,
    private errorHandlerService: ErrorHandlerService
  ) { }

  loadOrganisation(callback?: (organisation?: Organisation)=>void){
    if(this.loginService.isLoggedIn()){
      this.loadingOrganisation = true;
      this.getOrganisation().subscribe({
        next: (response: Organisation)=>{
          this.organisation = response;
          if(callback) callback(response);
          this.loadingOrganisation = false;
        },
        error: (error: any)=>{
          this.errorHandlerService.handleError(error, (message: string)=>{
            this.organisationError = {
              class: AlertType.ERROR,
              message: message
            }
          });
          this.loadingOrganisation = false;
          if(callback) callback();
        }
      })
    } else {
      if(callback) callback();
    }
  }

  getOrganisation(): Observable<Organisation>{
    return this.http.get<Organisation>(environment.serverUrl + "/organisations");
  }

  getAddresses(organisationID: string): Observable<Address[]>{
    return this.http.get<Address[]>(environment.serverUrl + `/organisations/${organisationID}/addresses`);
  }

  getContacts(organisationID: string): Observable<Contact[]>{
    return this.http.get<Contact[]>(environment.serverUrl + `/organisations/${organisationID}/contacts`);
  }

  getCountries(): Observable<Country[]>{
    return this.http.get<Country[]>(environment.serverUrl + "/references/countries");
  }

  getBusinessTypes(): Observable<BusinessType[]>{
    return this.http.get<BusinessType[]>(environment.serverUrl + "/references/businessTypes");
  }

  getWeekStartDays(): Observable<WeekDay[]>{
    return this.http.get<WeekDay[]>(environment.serverUrl + "/organisations/weekStartDays");
  }

  getAccountingCalendar(): Observable<Calendar[]>{
    return this.http.get<Calendar[]>(environment.serverUrl + "/organisations/accountingCalendars");
  }

  getStartCloseDeclarationRules(){
    return this.http.get<DeclarationRule[]>(environment.serverUrl + "/organisations/startCloseDeclarationRules");
  }

  /**
   * @deprecated It won't be used anymore, just backend requires it, that's why it's here
   */
  getOrderModes(): Observable<OrderMode[]>{
    return this.http.get<OrderMode[]>(environment.serverUrl + "/organisations/orderModes");
  }

  editOrganisation(organisationID: string, form: UntypedFormGroup): Observable<Organisation>{
    const options: any = {
      id: organisationID,
      name: form.controls.name.value,
      businessTypeID: form.controls.businessType.value,
      countryID: form.controls.country.value,
      eMailID: form.controls.emailID.value,
      phoneID: form.controls.phoneID.value,
      vatNumber: form.controls.vatNumber.value,
      companyRegistrationNumber: form.controls.regNumber.value,
      website: form.controls.website.value
    };

    return this.http.put<Organisation>(environment.serverUrl + `/organisations/${organisationID}`, options);
  }

  editEmail(id: string, form: UntypedFormGroup): Observable<Email>{
    const options: any = {
      id: id,
      address: form.controls.email.value,
      name: "Email",
      type: "Email"
    };

    return this.http.put<Email>(environment.serverUrl + "/references/eMails", options);
  }

  editPhone(id: string, form: UntypedFormGroup): Observable<Phone>{
    const options: any = {
      id: id,
      number: form.controls.phone.value,
      name: "Phone",
      type: "Phone"
    };

    return this.http.put<Phone>(environment.serverUrl + "/references/phones", options);
  }

  editSettings(organisationID: string, form: UntypedFormGroup): Observable<OrganisationSetting>{
    const options: any = {
      id: organisationID,
      accountingCalendarID: form.controls.accountingCalendar.value,
      allowEndOfDayWithOpenSales: form.controls.allowEndOfDay.value,
      dayDivide: this.convertBackTimeToString(form.controls.dayDivide.value),
      declarationAttemptsAllowed: form.controls.declarationAttempts.value,
      declarationToleranceValue: form.controls.declarationTolerance.value,
      financialYearEnd: (form.controls.dateRange.value ? moment(this.formatDateRangeToDate(form.controls.dateRange.value)[1], 'DD/MM/YYYY').format('MM/DD/YYYY') : moment().format('MM/DD/YYYY')),
      financialYearStart: (form.controls.dateRange.value ? moment(this.formatDateRangeToDate(form.controls.dateRange.value)[0], 'DD/MM/YYYY').format('MM/DD/YYYY') : moment().format('MM/DD/YYYY')),
      isDaySessionRequired: true,
      isPercentTolerance: form.controls.isPercentTolerance.value,
      orderModeID: form.controls.orderMode.value,
      showDeclarationAmount: form.controls.showDeclarationAmount.value,
      startCloseDeclarationRuleID: form.controls.declarationRule.value,
      timeZoneName: form.controls.organisationTimeZone.value,
      weekStartDayID: form.controls.weekStartDay.value,
      useSummaryReports: form.controls.useSummaryReports.value,
      useOldEOD: form.controls.useOldEOD.value,
      allowPluRanges: form.controls.allowPluRanges.value,
      useSixDigitPinCodes: form.controls.useSixDigitPinCodes.value,
      enableDigitFlash: form.controls.enableDigitFlash.value,
      isPricingAtModifierLevelEnabled: form.controls.isPricingAtModifierLevelEnabled.value,
      isProductAndCategoryLabelsEnabled: this.organisation.organisationSetting.isProductAndCategoryLabelsEnabled,
      hasTwoFactorAuthorisationEnabled: form.controls.hasTwoFactorAuthorisationEnabled.value,
      emailForTwoFactorAuthentication: form.controls.emailForTwoFactorAuthentication.value,
      appForTwoFactorAuthentication: form.controls.appForTwoFactorAuthentication.value,
      usesDeviceBasedLicensing: form.controls.usesDeviceBasedLicensing.value,
      useTaxGroupsCalc: form.controls.useTaxGroupsCalc.value,
      useUserFlowV2: form.controls.useUserFlowV2.value
    };
    this.formatDateRangeToDate(form.controls.dateRange.value);

    return this.http.put<OrganisationSetting>(environment.serverUrl + `/organisations/${organisationID}/organisationSetting`, options);
  }

  editAddress(form: UntypedFormGroup): Observable<Address>{
    const options: any = {
      id: form.controls.id.value,
      name: form.controls.description.value,
      line1: form.controls.line1.value,
      line2: form.controls.line2.value,
      line3: form.controls.line3.value,
      line4: form.controls.line4.value,
      line5: form.controls.line5.value,
      country: form.controls.country.value,
      isDeleted: form.controls.isDeleted.value,
      town: form.controls.town.value,
      postcode: form.controls.postcode.value,
      province: form.controls.province.value
    };

    return this.http.put<Address>(environment.serverUrl + `/references/addresses`, options);
  }

  createAddress(form: UntypedFormGroup, uuid: string){
    const options: any = {
      id: uuid,
      name: form.controls.description.value,
      line1: form.controls.line1.value,
      line2: form.controls.line2.value,
      line3: form.controls.line3.value,
      line4: form.controls.line4.value,
      line5: form.controls.line5.value,
      country: form.controls.country.value,
      isDeleted: false,
      town: form.controls.town.value,
      postcode: form.controls.postcode.value,
      province: form.controls.province.value
    };

    return this.http.post<Address>(environment.serverUrl + `/references/addresses`, options);
  }

  createAddressForSite(site: Site, uuid: string){
    const options: any = {
      abbreviatedName: site.abbreviatedName,
      addressID: uuid,
      contactID: site.contact?.id,
      emailID: site.eMail?.id,
      phoneID: site.phone?.id,
      displayName: site.displayName,
      id: site.id,
      name: site.name,
      reportingName: site.reportingName,
      vatNumber: site.vatNumber,
      website: site.website
    };

    return this.http.put<Address>(environment.serverUrl + `/sites/address`, options);
  }

  editTradingSession(organisationID: string, tradingSessionList: TradingSession[]): Observable<TradingSession[]>{
    return this.http.post<TradingSession[]>(environment.serverUrl + `/organisations/${organisationID}/tradingSessions/bulk`, tradingSessionList);
  }

  removeTradingSession(organisationID: string, tradingSession: TradingSession){
    const options: any = {
      body: {
        id: tradingSession.id
      }
    };

    return this.http.delete<TradingSession>(environment.serverUrl + `/organisations/${organisationID}/tradingSessions`, options);
  }

  createEmail(form: UntypedFormGroup, uuid: string){
    const options = {
      address: form.controls.email.value,
      id: uuid,
      name: "Email",
      type: "Generic"
    }

    return this.http.post<Email>(environment.serverUrl + `/references/eMails`, options);
  }

  createPhone(form: UntypedFormGroup, uuid: string){
    const options = {
      number: form.controls.phone.value,
      id: uuid,
      name: "Phone",
      type: "Phone"
    }

    return this.http.post<Phone>(environment.serverUrl + `/references/phones`, options);
  }

  createContact(form: UntypedFormGroup, uuid: string){
    const options = {
      address: null,
      eMail: null,
      id: uuid,
      name: form.controls.name.value,
      phone: null,
      position: form.controls.position.value
    }

    return this.http.post<Contact>(environment.serverUrl + `/references/contacts`, options);
  }

  createEmailForContact(contactID: string, form: UntypedFormGroup, emailUUID: string){
    const options = {
      address: form.controls.email.value,
      id: emailUUID,
      name: "Email",
      type: "Generic"
    }

    return this.http.patch<Email>(environment.serverUrl + `/references/contacts/${contactID}/eMail`, options);
  }

  createPhoneForContact(contactID: string, form: UntypedFormGroup, phoneUUID: string){
    const options = {
      id: phoneUUID,
      name: "Phone",
      number: form.controls.phone.value,
      type: "Phone"
    }

    return this.http.patch<Phone>(environment.serverUrl + `/references/contacts/${contactID}/phone`, options);
  }

  createContactForOrganisation(organisationID: string, contactUUID: string){
    const options = {
      id: contactUUID
    }

    return this.http.post<Contact>(environment.serverUrl + `/organisations/${organisationID}/contacts`, options);
  }

  createContactForSite(site: Site, contactUUID: string){
    const options = {
      abbreviatedName: site.abbreviatedName,
      addressID: site.address?.id,
      contactID: contactUUID,
      emailID: site.eMail?.id,
      phoneID: site.phone?.id,
      displayName: site.displayName,
      id: site.id,
      name: site.name,
      reportingName: site.reportingName,
      vatNumber: site.vatNumber,
      website: site.website
    }

    return this.http.put<Contact>(environment.serverUrl + `/sites`, options);
  }

  editContact(contactID: string, form: UntypedFormGroup){
    const options: any = {
      id: contactID,
      name: form.controls.name.value,
      position: form.controls.position.value,
      address: null,
      eMail: null,
      phone: null
    }

    return this.http.put<Contact>(environment.serverUrl + `/references/contacts`, options);
  }

  editPhoneForContact(contactID: string, form: UntypedFormGroup, uuid: string){
    const options: any = {
      id: uuid,
      number: form.controls.phone.value,
      name: "Phone",
      type: "Phone"
    }

    return this.http.patch<Phone>(environment.serverUrl + `/references/contacts/${contactID}/phone`, options);
  }

  editEmailForContact(contactID: string, form: UntypedFormGroup, uuid: string){
    const options: any = {
      id: uuid,
      address: form.controls.email.value,
      name: "Email",
      type: "Email"
    }

    return this.http.patch<Email>(environment.serverUrl + `/references/contacts/${contactID}/eMail`, options);
  }

  removeContact(organisationID: string, contact: Contact){
    const options: any = {
      body: {
        id: contact.id
      }
    };

    return this.http.delete<TradingSession>(environment.serverUrl + `/organisations/${organisationID}/contacts`, options);
  }

  formatDateRangeToDate(value: string): string[]{
    return value.split('-').map(startEnd => startEnd = startEnd.trim());
  }

  convertStringToTime(time: string){
    const timeArray = time.split(':');

    return {
      hour: parseInt(timeArray[0]),
      minute: parseInt(timeArray[1])
    }
  }

  convertBackTimeToString(value: {hour: number, minute: number}): string{
    // Safety check to make sure a string isn't passed in.
    if (typeof value === 'string') return value;

    const hour = value.hour.toString().length === 1 ? "0" + value.hour : value.hour;
    const minute = value.minute.toString().length === 1 ? "0" + value.minute : value.minute;
    const clock = hour + ":" + minute;

    return clock;
  }

  getOrganisationSummary(): Observable<OrganisationSummary>{
    return this.http.get<OrganisationSummary>(environment.serverUrl + "/organisations/organisationSummary");
  }

  // CommandCentre
  getAllOrganisation(): Observable<Organisation[]>{
    return this.http.get<Organisation[]>(environment.serverUrl + "/registrations/organisations");
  }

  getOrganisationSummaryByID(orgID: string): Observable<SingleOrgSummary>{
    return this.http.get<SingleOrgSummary>(environment.serverUrl + `/organisations/organisationSummary/${orgID}`);
  }

  getOrganisationSettingsByID(orgID: string): Observable<OrganisationSetting>{
    return this.http.get<OrganisationSetting>(environment.serverUrl + `/organisations/${orgID}/organisationSetting`);
  }

  createOrganisation(form: UntypedFormGroup){
    const options = {
      id: form.controls.id.value,
      name: form.controls.name.value,
      countryID: form.controls.countryID.value,
      licenses: form.controls.licenses.value,
      customerType: form.controls.customerType.value
    }
    return this.http.post<NewOrganisation>(environment.serverUrl + "/registrations/database", options);
  }

  toggleOrganisation(orgID: string){
    return this.http.delete<NewOrganisation>(environment.serverUrl + `/registrations/database/${orgID}`);
  }

  adjustLicenses(orgID: string, subscriptionId: string, quantity: number) {
    return this.http.put<any>(environment.serverUrl + `/subscriptions/${orgID}/${subscriptionId}/${quantity}`, null);
  }

   adjustChargeRate(newLicense:UntypedFormGroup, orgID:string){
    const options = {
      organisationID: orgID,
      chargeRatePerc: newLicense.controls.stripeChargePerc.value,
      chargeRateAmount: newLicense.controls.stripeChargeAmount.value
    }
    return this.http.post<any>(environment.serverUrl + `/registrations/adjustChargeRate/${orgID}/${newLicense.controls.stripeChargePerc.value}/${newLicense.controls.stripeChargeAmount.value}`, options);
  }

  getSubscriptions(orgID: string) {
    return this.http.get<any>(environment.serverUrl + `/subscriptions/org/${orgID}`);
  }

  updateOrganisationModule(orgID: string, isModuleEnabled: boolean) {
    return this.http.put<Organisation>(environment.serverUrl + `/organisations/organisationModule/${orgID}/${isModuleEnabled}`, null);
  }
}
